import { Trans, useTranslation } from 'react-i18next';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_CAPTION_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type {
  tWizardErrorCatalogEntry,
  tWizardExternalLinkVariant,
} from './WhatsAppConnectionWizard.types';
import WhatsAppConnectionWizardExternalLink, {
  WhatsAppConnectionWizardAdvisorLink,
} from './WhatsAppConnectionWizardExternalLink.component';

export type tWhatsAppConnectionWizardErrorStepsProps = {
  entry: tWizardErrorCatalogEntry;
  /** With Meta's window open, the last step starts by closing it. */
  isMetaWindowOpen: boolean;
  /** Whether the entry's Meta link shows; false where the footer carries it. */
  showsMetaLink: boolean;
  /** The line to have ready before talking to an advisor, above the advisor. */
  supportLine?: string;
  /** The advisor's weight here, or undefined to leave it out. */
  advisorVariant?: Exclude<tWizardExternalLinkVariant, 'primary'>;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * How to fix a Meta error from the catalog: who acts, the steps, and the
 * links that are not in the footer. The main pane and the aside show it the
 * same way.
 *
 * @param {tWhatsAppConnectionWizardErrorStepsProps} props - The entry and
 * what this placement adds to its steps.
 * @returns {JSX.Element} The entry's steps and links.
 */
export default function WhatsAppConnectionWizardErrorSteps(
  props: tWhatsAppConnectionWizardErrorStepsProps,
) {
  const {
    entry,
    isMetaWindowOpen,
    showsMetaLink,
    supportLine,
    advisorVariant,
    onAdvisorCtaClick,
  } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const entryKey = `wizard.errorCatalog.${entry.id}`;
  const stepNumbers = Array.from(
    { length: entry.stepCount },
    (_, index) => index + 1,
  );
  // Only an entry that opens Meta has a page to link: the Facebook page of a
  // number has no link to open yet.
  const metaUrl =
    showsMetaLink && entry.footerPrimary === 'openInMeta'
      ? entry.metaUrl
      : undefined;

  return (
    <>
      <h4 className="text-[length:var(--ls-body)] font-medium text-[var(--ls-text-neutral-default)]">
        {t(`${entryKey}.whoActs`)}
      </h4>
      <ol
        className={`flex list-decimal flex-col gap-[var(--ls-spacing-2)] pl-[var(--ls-spacing-5)] ${WIZARD_BODY_CLASS_NAME}`}
      >
        {stepNumbers.map((stepNumber) => {
          // The retry can only start once Meta's window is closed.
          const isLastStepWithWindowOpen =
            isMetaWindowOpen && stepNumber === entry.stepCount;
          return (
            <li key={stepNumber}>
              <Trans
                t={t}
                i18nKey={
                  isLastStepWithWindowOpen
                    ? `${entryKey}.lastStepWindowOpen`
                    : `${entryKey}.steps.${stepNumber}`
                }
                // Meta's menu names stand out in medium weight; a bare strong
                // renders bolder.
                components={{ b: <strong className="font-medium" /> }}
              />
            </li>
          );
        })}
      </ol>
      {metaUrl && (
        <div>
          <WhatsAppConnectionWizardExternalLink
            href={metaUrl}
            label={t(`${entryKey}.metaLinkLabel`)}
            variant="secondary"
          />
        </div>
      )}
      {(supportLine || advisorVariant) && (
        <div className="flex flex-col items-start gap-[var(--ls-spacing-2)]">
          {supportLine && (
            <p className={WIZARD_CAPTION_CLASS_NAME}>{supportLine}</p>
          )}
          {advisorVariant && (
            <WhatsAppConnectionWizardAdvisorLink
              variant={advisorVariant}
              onAdvisorCtaClick={onAdvisorCtaClick}
            />
          )}
        </div>
      )}
    </>
  );
}
